import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { TextInput } from '../primitives/TextInput';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfLayoutProps } from './rhfLayout';

export interface RhfTimePickerProps extends RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  id?: string;
  /** Minimum time in HH:mm format (24-hour). */
  min?: string;
  /** Maximum time in HH:mm format (24-hour). */
  max?: string;
  /**
   * Step in seconds. Defaults to 60 (1 minute).
   * Set to 1 for seconds precision, 900 for 15-minute intervals, etc.
   */
  step?: number;
}

/**
 * Time picker adapter for React Hook Form.
 *
 * Uses a native `<input type="time">` wrapped in the DS TextInput for consistent styling.
 * Stores the value as an HH:mm string (24-hour format).
 */
export const RhfTimePicker = React.forwardRef<HTMLInputElement, RhfTimePickerProps>(
  function RhfTimePicker(
    {
      control,
      name,
      label,
      hint,
      required = false,
      semanticId,
      disabled = false,
      id,
      min,
      max,
      step = 60,
      controlWidth,
    },
    ref,
  ) {
    const controlId = id ?? name;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              label={label}
              htmlFor={controlId}
              hint={hint}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <TextInput
                ref={composeRefs(field.ref, ref)}
                type="time"
                id={controlId}
                name={field.name}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                error={Boolean(errorText)}
                semanticId={semanticId}
                min={min}
                max={max}
                step={step}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfTimePicker.displayName = 'RhfTimePicker';
